import { Component, ViewChild, ElementRef, OnDestroy, AfterViewInit, Inject, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';

import { AttachmentKind } from 'src/app/interfaces';
import { UtilsService } from 'src/app/services/utils.service';

const DEFAULT_ALLOWED_KINDS: AttachmentKind[] = [AttachmentKind.IMAGE];

/**
 * API rejects a request body over 32 MiB and the API reads the whole upload into memory,
 * so a file past this point fails server-side with no useful message.
 */
const DEFAULT_MAX_FILE_SIZE_MB = 20;

const BYTES_PER_MB = 1024 * 1024;

export interface TakePictureDialogData {
  // When true, shows a "choose from gallery/filesystem" option alongside the camera capture.
  // Left undefined (falsy) for flows that must stay camera-only for fraud prevention (e.g. creating a new issue).
  allowGallery?: boolean;
  // Kinds of file the picker accepts. Images only when unset.
  allowedKinds?: AttachmentKind[];
  // Largest file the picker accepts, in megabytes.
  maxFileSizeMb?: number;
}

@Component({
  selector: 'app-take-picture-dialog',
  templateUrl: './take-picture-dialog.component.html',
  styleUrls: ['./take-picture-dialog.component.scss']
})
export class TakePictureDialogComponent implements AfterViewInit, OnDestroy {

  loadingCamera: boolean = true;
  cameraError: boolean = false;
  camera: MediaStream | null = null;

  dialogRef: MatDialogRef<TakePictureDialogComponent>;

  readonly allowGallery: boolean;
  readonly fileAccept: string;
  readonly titleKey: string;
  readonly galleryButtonKey: string;
  readonly galleryButtonIcon: string;

  private readonly allowedKinds: AttachmentKind[];
  private readonly maxFileSizeMb: number;

  private readonly utilsService = inject(UtilsService);
  private readonly translateService = inject(TranslateService);

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

  constructor(
    matDialogRef: MatDialogRef<TakePictureDialogComponent>,
    private spinner: NgxSpinnerService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: TakePictureDialogData | null,
  ) {
    this.dialogRef = matDialogRef;
    this.allowGallery = !!data?.allowGallery;
    this.allowedKinds = data?.allowedKinds?.length ? data.allowedKinds : DEFAULT_ALLOWED_KINDS;
    this.maxFileSizeMb = data?.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
    this.fileAccept = this.utilsService.attachmentAccept(this.allowedKinds);

    const imagesOnly = this.allowedKinds.length === 1 && this.allowedKinds[0] === AttachmentKind.IMAGE;
    this.titleKey = imagesOnly
      ? 'components.take_picture.title'
      : 'components.take_picture.title_attachment';
    this.galleryButtonKey = imagesOnly
      ? 'components.take_picture.choose_from_gallery'
      : 'components.take_picture.choose_file';
    this.galleryButtonIcon = imagesOnly ? 'ph-regular-images' : 'ph-regular-paperclip';
  }

  ngAfterViewInit(): void {
    this.initializeCamera();
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  private async initializeCamera(): Promise<void> {
    this.loadingCamera = true;
    this.cameraError = false;
    try {
      const constraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'environment'
        }
      };
      this.camera = await navigator.mediaDevices.getUserMedia(constraints);
      this.loadingCamera = false;

      // NO acceder a videoElement aquí, se configurará automáticamente cuando el template se renderice
    }
    catch (error) {
      console.error('Error al acceder a la cámara:', error);
      this.loadingCamera = false;
      this.cameraError = true;
      this.camera = null;
    }
  }

  retryCamera(): void {
    this.initializeCamera();
  }

  takePicture(): void {
    if (!this.camera || !this.videoElement?.nativeElement || !this.canvasElement?.nativeElement) {
      return;
    }
    this.spinner.show();

    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Establecer las dimensiones del canvas igual que el video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Dibujar el frame actual del video en el canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convertir el canvas a blob
    canvas.toBlob((blob) => {
      if (blob) {
        // Crear un archivo a partir del blob
        const file = new File([blob], `photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });

        // Cerrar el diálogo y devolver el archivo
        this.closeDialog(file);
      }
    }, 'image/jpeg', 0.8);
  }

  onGallerySelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;
    // The input's `accept` is a filter on what the picker offers, not on what it returns: a
    // desktop picker hands back any file chosen through "All files".
    if (!this.utilsService.isAcceptedAttachment(file, this.allowedKinds)) {
      this.showError('components.take_picture.invalid_type', { kinds: this.allowedKindsSummary() });
      return;
    }
    if (file.size > this.maxFileSizeMb * BYTES_PER_MB) {
      this.showError('components.take_picture.file_too_large', { max: this.maxFileSizeMb });
      return;
    }
    this.closeDialog(file);
  }

  closeDialog(photo: File): void {
    this.stopCamera();
    this.spinner.hide();
    this.dialogRef?.close(photo);
  }

  /** Reads as "imágenes, videos o PDF" — the kinds this dialog was configured to accept. */
  private allowedKindsSummary(): string {
    const labels = this.allowedKinds.map(
      kind => this.translateService.instant(`common.attachments.kinds.${kind}`)
    );
    if (labels.length === 1) return labels[0];
    const conjunction = this.translateService.instant('common.or');
    return `${labels.slice(0, -1).join(', ')} ${conjunction} ${labels[labels.length - 1]}`;
  }

  private showError(key: string, params: Record<string, unknown>): void {
    this.snackBar.open(
      this.translateService.instant(key, params),
      this.translateService.instant('common.close'),
      { duration: 5000 },
    );
  }

  private stopCamera(): void {
    if (this.camera) {
      this.camera.getTracks().forEach(track => track.stop());
      this.camera = null;
    }
  }

}
